import React, { createContext, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useCategoryContext } from "./CategoryContext";
import { RoundResult } from "../interfaces/ICompetitions";

interface RoundContextValue {
  round: RoundResult | null;
  competition: ReturnType<typeof useCategoryContext>["competition"];
  category: ReturnType<typeof useCategoryContext>["category"];
  loading: boolean;
  error: string | null;
}

const RoundContext = createContext<RoundContextValue | undefined>(undefined);

export const useRoundContext = () => {
  const context = useContext(RoundContext);
  if (!context) {
    throw new Error("useRoundContext must be used within RoundContextProvider");
  }
  return context;
};

interface RoundContextProviderProps {
  children: React.ReactNode;
}

export const RoundContextProvider: React.FC<RoundContextProviderProps> = ({ children }) => {
  const { roundId } = useParams<{ roundId: string }>();
  const { category, competition, loading, error } = useCategoryContext();

  const round = useMemo(() => {
    if (!category || !roundId) return null;
    return category.rounds.find((r) => r.roundId === roundId) || null;
  }, [category, roundId]);

  const value: RoundContextValue = useMemo(
    () => ({
      round,
      competition,
      category,
      loading,
      error: error || (roundId && !round ? "Nie znaleziono rundy" : null),
    }),
    [round, competition, category, loading, error, roundId],
  );

  return <RoundContext.Provider value={value}>{children}</RoundContext.Provider>;
};

